const COUNTRY_CODE_PARAM = 'countryCode'
const DEFAULT_COUNTRY_CODE = 'nl'
const COUNTRY_CODE_PATTERN = /^[a-z]{2}$/i
const LOCALE_PATH_PATTERN = /^\/([a-z]{2}-([a-z]{2}))(?:\/|$)/i

export function normalizeCountryCode(
  value?: string | null,
): string | undefined {
  const normalized = value?.replace(/['"]/g, '').trim().toUpperCase()
  return normalized && COUNTRY_CODE_PATTERN.test(normalized)
    ? normalized
    : undefined
}

function getCurrentUrl(): URL | undefined {
  if (typeof window === 'undefined') return undefined

  return new URL(window.location.href)
}

function getCountryCodeParamFromUrl(url: URL): string | undefined {
  return normalizeCountryCode(url.searchParams.get(COUNTRY_CODE_PARAM))
}

function getLocaleCountryCodeFromUrl(url: URL): string | undefined {
  const localeCountryCode = url.pathname.match(LOCALE_PATH_PATTERN)?.[2]
  return normalizeCountryCode(localeCountryCode)
}

/**
 * The locale segment of the host page's path, e.g. `/en-gb/account` → `en-gb`.
 *
 * A storefront link built without it is redirected to the default locale,
 * `en-us`, which silently changes country: a GB customer sent to `/returns`
 * lands on `/en-us/returns` and is offered the US returns provider. Returns
 * `undefined` when the path carries no locale — every in-store host — so those
 * links stay exactly as they are.
 */
export function getLocalePathPrefixFromUrl(
  url = getCurrentUrl(),
): string | undefined {
  return url?.pathname.match(LOCALE_PATH_PATTERN)?.[1]?.toLowerCase()
}

export function getCountryCodeFromUrl(
  url = getCurrentUrl(),
): string | undefined {
  if (!url) return undefined

  return getCountryCodeParamFromUrl(url) ?? getLocaleCountryCodeFromUrl(url)
}

export function getResolvedCountryCode(
  customerCountryCode?: string | null,
  url = getCurrentUrl(),
): string {
  return (
    (url && getCountryCodeParamFromUrl(url)) ??
    (url && getLocaleCountryCodeFromUrl(url)) ??
    normalizeCountryCode(customerCountryCode) ??
    DEFAULT_COUNTRY_CODE
  )
}
